import { Component, EventEmitter, Input, Output, OnInit, OnDestroy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { isEqual } from 'lodash';
import { Coupon, CouponData, CouponError, CouponRequest } from 'src/app/interfaces';
import { Logger } from 'src/app/services/logger.service';
import { StripeService } from 'src/app/services/stripe.service';
import { REQUIRED_ERROR } from 'src/app/validators.constants';
import Subscriber from 'src/app/subscriber';
import { OrderData } from '@functions/interfaces';

const log = new Logger('SectionCartComponent');
const sub = new Subscriber();

@Component({
  selector: 'app-coupon',
  templateUrl: './coupon.component.html',
  styleUrls: ['./coupon.component.scss'],
})
export class CouponComponent implements OnInit, OnDestroy {
  @Input() orderId: OrderData['orderId'];

  @Input() set coupon(coupon: CouponData | undefined) {
    if (!isEqual(this.coupon, coupon)) {
      this._coupon = coupon;
      this.couponChange.emit(coupon);
    }
  }

  get coupon(): CouponData {
    return this._coupon;
  }

  @Output() couponChange: EventEmitter<CouponData> = new EventEmitter();

  private _coupon: CouponData | undefined;

  // * couponId FormControl
  public couponIdControl = new FormControl('', [Validators.required]);

  // * Errors
  private _error: string = REQUIRED_ERROR;

  get error(): string {
    const controlErrors = this.couponIdControl.errors;
    const error = this._error;

    if (!error && controlErrors) {
      if (controlErrors.required) {
        this._error = REQUIRED_ERROR;
      } else if (controlErrors.forbidden) {
        this._error = controlErrors.forbidden;
      }
    }

    return this._error;
  }

  set error(error: string) {
    this._error = error;

    if (error) {
      this.couponIdControl.setErrors({ forbidden: error });
    }
  }

  get isCouponError(): boolean {
    return 'error' in this.coupon;
  }

  constructor(
    private _stripeService: StripeService,
  ) {}

  ngOnInit(): void {
    sub.push(
      this._stripeService.couponAdd.subscribe((obj: {
        [orderId: string]: Coupon | CouponRequest;
      }) => {
        // check if it was added for this order
        const coupon = obj[this.orderId];

        if (coupon) {
          this.coupon = coupon;
          log.info('coupon added', coupon);
        }
      }),

      this._stripeService.couponUpdate.subscribe((obj: {
        [orderId: string]: CouponData;
      }) => {
        const coupon = obj[this.orderId];

        if (coupon) {
          this.coupon = coupon;
          log.info('coupon updated', coupon);
        }
      }),

      this._stripeService.couponError.subscribe((obj: {
        [orderId: string]: CouponError;
      }) => {
        // * there's no need to update this.coupon
        const coupon = obj[this.orderId];

        // check if this error for our coupon
        if (coupon) {
          this.error = coupon.error;
          log.info('coupon error', coupon);
        }
      }),

      this.couponIdControl.valueChanges.subscribe((value: string) => {
        // isCouponError getter doesn`t work in this case :c
        if (this.coupon && 'error' in this.coupon && this.coupon.id === value) {
          this.error = this.coupon.error;
        } else {
          this.error = '';
        }

        if (this.coupon && !this.isCouponError) {
          if (this.couponIdControl.enabled) {
            this.couponIdControl.disable();
          }
        } else {
          if (this.couponIdControl.disabled) {
            this.couponIdControl.enable();
          }
        }
      }),
    )
  }

  ngOnDestroy(): void {
    sub.unsubscribe();
  }

  // public onApply() {
  //   this.apply.emit(this.coupon.value)
  // }

  /**
   * Applies coupon to the order by `couponId`
   */
  async redeemCoupon() {
    this._stripeService.redeemCoupon(this.orderId, this.couponIdControl.value);
  }
}
